import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";

// ------- Palette -------
// paper: #F1EBDF   ink: #1F1A14   brass: #B08056   taupe: #7A6F5F   line: #D9CFBE

const STUDIO = {
    name: "Maulik Prajapati",
    role: "Interior Designer",
    email: "mvprajapati.137890@gmail.com",
    phone: "+91 87589 65327",
    location: "Ahmedabad, Gujarat · India",
};

const PROFILE_PHOTO =
    "https://customer-assets.emergentagent.com/job_minimal-design-62/artifacts/tqk6cew8_WhatsApp%20Image%202026-06-15%20at%2010.15.48%20PM.jpeg";

const NATURE_IMG =
    "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=80&w=1600&auto=format&fit=crop";

const A = "https://customer-assets.emergentagent.com/job_minimal-design-62/artifacts/";

// ------- Academic projects (single source of truth for the book) -------
const PROJECTS = [
    {
        id: "ahmedabad",
        titleLines: ["Ahmedabad Heritage", "Café & Restaurant."],
        category: "Restaurant & café",
        year: "2026",
        subtitle:
            "A dining destination inspired by the heritage, culture and timeless identity of Ahmedabad.",
        description: [
            "Inspired by the rich architectural heritage, vibrant culture and timeless identity of Ahmedabad — an immersive dining experience that lets visitors connect with the city's history within a contemporary hospitality environment.",
            "Traditional pol houses, intricate jali patterns, heritage materials and local craftsmanship become modern interventions. A dedicated holographic zone narrates Ahmedabad's story, turning the restaurant into a destination for dining and cultural exploration.",
        ],
        materialBoard: `${A}n22vv5ta_6.png`,
        gallery: [
            { src: `${A}x1xck7r2_1.png`, title: "Ground floor plan" },
            { src: `${A}3tbh6h7y_2.png`, title: "First floor plan" },
            { src: `${A}5zkhk4v9_3.png`, title: "Facade elevations" },
            { src: `${A}5vkggn40_4.png`, title: "Heritage facade detail" },
            { src: `${A}p40oqjsy_5.png`, title: "Kitchen & service areas" },
        ],
    },
    {
        id: "office",
        titleLines: ["Tranquil Workspace —", "IT Office."],
        category: "Workplace",
        year: "2026",
        subtitle:
            "A contemporary IT office designed around calmness, focus and employee well-being.",
        description: [
            "The office is reimagined as a balanced workspace that promotes productivity while reducing visual and mental stress — clean architectural lines, natural materials, soft palettes and thoughtfully planned spatial arrangements.",
            "Open workstations, collaborative zones, meeting spaces and breakout areas are integrated to encourage both concentration and teamwork. Biophilic elements, natural light and warm textures give the interior a comfortable, human-centric atmosphere.",
        ],
        materialBoard: `${A}49tphawz_Color%20pallate.png`,
        gallery: [
            { src: `${A}nmma8k7a_1.png`, title: "Option 1 — Floor Plan" },
            { src: `${A}dhnzw9vs_1%20A.png`, title: "Option 1 — Zonning" },
            { src: `${A}haav0xki_2.png`, title: "Option 2 — Floor Plan" },
            { src: `${A}trfmt4hp_2%20A.png`, title: "Option 2 — Zonning" },
            { src: `${A}rvij3h01_3.png`, title: "Option 3 — Floor Plan" },
            { src: `${A}9cqtzg9a_3%20A.png`, title: "Option 3 — Zonning" },
            { src: `${A}r67gxqbe_4.png`, title: "Workstation elevation" },
        ],
    },
    {
        id: "retail",
        titleLines: ["Curve & Comfort —", "Garment Showroom."],
        category: "Retail showroom",
        year: "2026",
        subtitle:
            "A contemporary garment showroom inspired by curved forms and natural flow.",
        description: [
            "The design blends functionality with aesthetics, creating an inviting space that enhances browsing, interaction and brand connection.",
            "Flowing arched portals, fluted wood panels and curved counters guide the customer through the floor — moving naturally from entry, past the central display, into the trial room and back out via reception.",
        ],
        materialBoard: `${A}qjgqqcd4_Color%20palette.png`,
        gallery: [
            { src: `${A}k7wjerf5_1.png`, title: "Circulation plan" },
            { src: `${A}kq74bave_2.png`, title: "Furniture layout" },
            { src: `${A}02hneezm_3.png`, title: "False ceiling layout" },
            { src: `${A}0sc6f89r_4.png`, title: "Display elevation" },
            { src: `${A}3wll11wt_5.png`, title: "Reception elevation" },
            { src: `${A}lgl7do9a_6.png`, title: "Electrical layout" },
            { src: `${A}hdokt69e_7.png`, title: "Lighting circuit plan" },
        ],
    },
    {
        id: "spa",
        titleLines: ["Essence Spa —", "Restore. Rebalance. Renew."],
        category: "Wellness · Spa",
        year: "2026",
        subtitle:
            "A wellness retreat designed around simplicity, harmony and sensory experience.",
        description: [
            "The interior draws inspiration from water, stones, nature, aroma and the play of light and shadow, layered with natural stone, light wood, lime plaster, linen fabric and trickling water features.",
            "The spa is shaped as a sequence — Arrival, Welcome, Purify, Indulge, Relax, Renew — so every guest moves through a curated journey from the outside world into deep, holistic stillness.",
        ],
        materialBoard: `${A}phqlaxrx_Color%20palette.png`,
        gallery: [
            { src: `${A}4kgtnkcg_1.png`, title: "Zoning plan" },
            { src: `${A}2r8y2vpk_2.png`, title: "Floor plan" },
            { src: `${A}0jn0cl13_3.png`, title: "Facade elevation" },
            { src: `${A}ne6qr3eg_4.png`, title: "Treatment-room elevation" },
        ],
    },
    {
        id: "salon",
        titleLines: ["Golden Aura", "Salon."],
        category: "Salon",
        year: "2026",
        subtitle:
            "A contemporary luxury salon offering an exceptional beauty experience.",
        description: [
            "The design blends elegance, comfort and functionality with a refined palette of white and metallic finishes, creating a timeless and sophisticated ambience.",
            "Reception, hair-styling, nail and manicure-pedicure stations, a wash area and a private service room are organised around a clear primary circulation, with a generous storefront framed by brass columns and statement mirrors.",
        ],
        materialBoard: `${A}pppz8s2b_Color%20palette.png`,
        gallery: [
            { src: `${A}dn4y9ard_1.png`, title: "Circulation plan & floor plan" },
            { src: `${A}nugwf6jq_2.png`, title: "Furniture layout" },
            { src: `${A}nfih1i2v_3.png`, title: "Reception-counter elevation" },
            { src: `${A}oi6simdb_4.png`, title: "Styling-station elevation" },
        ],
    },
];

const NR_COVER =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/smfxwzi9_ChatGPT%20Image%20Jul%2027%2C%202026%2C%2005_43_14.png";
const VJ_COVER =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/zb6w54g8_ChatGPT%20Image%20Jul%2027%2C%202026%2C%2011_30_38%20AM.png";
const B = "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/";

const RESIDENTIAL_PROJECTS = [
    {
        id: "newranip",
        titleLines: ["New Ranip Residence,", "Ahmedabad."],
        category: "Residential · Private residence",
        year: "2026",
        subtitle: "A luxurious 2 BHK residence — elegance, comfort, sophistication.",
        description: [
            "A private residence in New Ranip focused on modular joinery, generous storage and a warm, ritual-forward living space.",
            "The brief covers kitchen, master bedroom, built-in wardrobe and the living-room feature wall — developed with a coherent palette and detailed working drawings.",
        ],
        brief: [
            { k: "Category", v: "Residential · Private residence" },
            { k: "Configuration", v: "2 BHK" },
            { k: "Carpet area", v: "178 sq. ft." },
            { k: "Location", v: "New Ranip, Ahmedabad" },
            { k: "Style", v: "Modern + Traditional" },
            { k: "Scope", v: "Kitchen · Master bedroom · Wardrobe · Living-room feature wall" },
            { k: "Status", v: "Execution stage" },
        ],
        materialBoard: NR_COVER,
        gallery: [
            { src: `${B}byvs8emt_Screenshot%202026-07-25%20202213.png`, title: "Kitchen elevation" },
            { src: `${B}tiy90qvb_Screenshot%202026-07-25%20202234.png`, title: "Bedroom floor plan" },
            { src: `${B}0tpr356a_Screenshot%202026-07-25%20202255.png`, title: "Living-room TV & pooja unit" },
            { src: `${B}lfwnl7k4_Screenshot%202026-07-25%20202331.png`, title: "Wardrobe elevation" },
        ],
    },
    {
        id: "vejalpur",
        titleLines: ["Vejalpur Residence,", "Ahmedabad."],
        category: "Residential · Private residence",
        year: "2026",
        subtitle: "Residence at Ahmedabad — modern warm luxury residence.",
        description: [
            "A working set of drawings covering the kitchen, living room and bedrooms of a Vejalpur residence in full detail.",
            "The scope focuses on execution-ready drawings — floor plans, fixture layouts, switch / socket looping and dimensioned elevations that the on-site team can build to without ambiguity.",
        ],
        brief: [
            { k: "Category", v: "Residential · Private residence" },
            { k: "Configuration", v: "2 BHK" },
            { k: "Location", v: "Vejalpur, Ahmedabad" },
            { k: "Style", v: "Modern warm luxury" },
            { k: "Scope", v: "Kitchen · Living room · 2 Bedrooms · Bathrooms · Walk-in dressing" },
            { k: "Deliverables", v: "Floor plans · Fixture & looping plans · Dimensioned elevations" },
            { k: "Status", v: "Execution stage" },
        ],
        materialBoard: VJ_COVER,
        gallery: [
            { src: `${B}5wnmowin_Screenshot%202026-07-27%20104346.webp`, title: "Overall floor plan" },
            { src: `${B}qffegbx6_Kitchen_Fixture.webp`, title: "Kitchen — Fixture plan" },
            { src: `${B}f8kis1ov_Kitchen_Looping.webp`, title: "Kitchen — Looping plan" },
            { src: `${B}8s2bdvg7_LivingRoom_Fixture.webp`, title: "Living room — Fixture plan" },
            { src: `${B}kilgzkh5_Livingroom_looping.webp`, title: "Living room — Looping plan" },
            { src: `${B}hutfv4kd_Room1_Fixture.webp`, title: "Room 1 — Fixture plan" },
            { src: `${B}d26c5h4h_Room1_Looping.webp`, title: "Room 1 — Looping plan" },
            { src: `${B}fh3ionm5_Room2_Fixture.webp`, title: "Room 2 — Fixture plan" },
            { src: `${B}6dyrmm9i_Room2_looping.webp`, title: "Room 2 — Looping plan" },
            { src: `${B}jbv4rqdk_Kitchen.png`, title: "Kitchen elevation" },
            { src: `${B}ekh9gpiz_Room%202_.png`, title: "Kavinbhai Room — bed & TV elevation" },
            { src: `${B}ph7657xi_Room1.png`, title: "Son Room — bed & TV elevation" },
            { src: `${B}0lqu8jle_Bathroom_Walkin_dressing_1.png`, title: "Bathroom elevation — Room 2" },
            { src: `${B}owcg2vxk_Bathroom_Walkin_dressing_2.png`, title: "Bathroom elevation — outside" },
        ],
    },
];

// ------- Static pages (index & commercial) -------
const IndexPage = ({ pageNumber, total, sections, onJump }) => (
    <PageFrame section="Contents" pageNumber={pageNumber} total={total}>
        <div className="h-full flex items-center relative z-20">
            <ul className="w-full divide-y divide-[#D9CFBE] border-y border-[#D9CFBE]" data-testid="index-list">
                {sections.map((s, i) => (
                    <li key={i}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onJump) onJump(s.pageIndex);
                            }}
                            data-testid={`index-jump-${i}`}
                            className="group relative z-30 pointer-events-auto w-full py-2.5 flex items-baseline gap-3 text-left cursor-pointer hover:bg-[#EAE2D2] transition-colors duration-300 px-2 -mx-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#1F1A14]"
                        >
                            <span className="font-serif text-[#B08056] text-base w-8 shrink-0">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="font-serif text-[#1F1A14] text-[15px] md:text-[17px] group-hover:text-[#B08056] transition-colors truncate">
                                {s.label}
                            </span>
                            <span className="flex-1 mx-3 border-t border-dotted border-[#B08056]/50 translate-y-[-4px]" />
                            <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-[#7A6F5F] shrink-0">
                                Page {String(s.page).padStart(2, "0")}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    </PageFrame>
);

const COMMERCIAL_PROJECTS = [
    {
        id: "bricks-office",
        titleLines: ["The Bricks Gallery —", "Commercial Office."],
        category: "Commercial · Office + display",
        year: "2026",
        subtitle:
            "A compact 400 sq. ft. workplace for a bricks & cladding studio — a working office that quietly doubles as a material gallery.",
        description: [
            "An office designed for a bricks and cladding-material supplier, where the interior itself becomes a small display for the studio's own product. Brick and cladding samples are woven into the reception, feature wall and shelving so every surface earns its place.",
            "The 400 sq. ft. plan is organised around four workstations with a compact pantry, and a private boss cabin sized for an additional three-person conference seating — a discreet meeting zone within a lean footprint.",
            "The project is now complete on paper — floor plans, elevations and the material board are finalised and ready for execution.",
        ],
        materialBoard:
            "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/9e8d6hqs_ChatGPT%20Image%20Jul%2030%2C%202026%2C%2009_20_33%20AM.png",
        brief: [
            { k: "Category", v: "Bricks & cladding · Office" },
            { k: "Area", v: "400 sq. ft." },
            { k: "Display", v: "Small in-office material showcase" },
            { k: "Team", v: "4 workstations + pantry" },
            { k: "Boss cabin", v: "Private, with 3-person conference seating" },
            { k: "Status", v: "Completed" },
        ],
        gallery: [
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/16h0l25e_Screenshot%202026-07-30%20090818.webp",
                title: "Floor plan — Layout A",
            },
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/m3b9a5fo_Screenshot%202026-07-30%20090838.webp",
                title: "Floor plan — Layout B",
            },
        ],
    },
];

const THREE_D_VIS = [
    {
        group: "The Bricks Gallery",
        images: [
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/pya0ej0f_The%20Bricks%20Gallery%201.png",
                title: "Isometric layout views",
            },
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/m8073xir_The%20Bricks%20Gallery%202.png",
                title: "Reception & interior perspectives",
            },
        ],
    },
    {
        group: "Kitchen Project",
        images: [
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/dh2g66xs_Kitchen%201.png",
                title: "Open plan — kitchen & dining composition",
            },
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/n6f7gvjq_Kitchen%202.png",
                title: "Function meets minimalism — detail crops",
            },
            {
                src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/yoyin3a1_Kitchen%203.png",
                title: "Extended composition — all angles",
            },
        ],
    },
];

const CommercialBrief = ({ project, pageNumber, total }) => (
    <PageFrame
        section="Commercial · 01 · Completed"
        pageNumber={pageNumber}
        total={total}
    >
        <div className="grid grid-cols-12 gap-8 md:gap-10 h-full items-center">
            <div className="col-span-6 flex flex-col justify-center">
                <Overline>Commercial project · {project.year}</Overline>
                <h1 className="mt-4 font-serif font-light text-[#1F1A14] text-[26px] md:text-[36px] leading-[1.05] tracking-tight">
                    {project.titleLines.map((line, i) => (
                        <span key={i}>
                            {line}
                            {i < project.titleLines.length - 1 ? <br /> : null}
                        </span>
                    ))}
                </h1>
                <p className="mt-4 font-serif italic text-[#7A6F5F] text-[13px] md:text-[15px] leading-relaxed">
                    {project.subtitle}
                </p>
                <div className="mt-4 h-px w-12 bg-[#B08056]" />
                <div className="mt-4 space-y-3 max-w-md">
                    {project.description.map((p, i) => (
                        <p
                            key={i}
                            className="font-sans text-[#4A4237] leading-[1.6] text-[12px] md:text-[13px] text-justify"
                        >
                            {p}
                        </p>
                    ))}
                </div>
            </div>

            <div className="col-span-6 h-full flex flex-col justify-center">
                <Overline>Brief · at a glance</Overline>
                <dl className="mt-6 divide-y divide-[#D9CFBE] border-y border-[#D9CFBE]">
                    {project.brief.map((row, i) => (
                        <div key={i} className="py-4 flex items-baseline gap-6">
                            <dt className="text-[10px] tracking-[0.3em] uppercase text-[#B08056] w-32 shrink-0">
                                {row.k}
                            </dt>
                            <dd className="font-serif text-[#1F1A14] text-[15px] md:text-[17px] flex-1">
                                {row.v}
                            </dd>
                        </div>
                    ))}
                </dl>
                <p className="mt-8 font-serif italic text-[#7A6F5F] text-[13px] max-w-md">
                    Design phase completed — floor plans, elevations and material board finalised
                    for on-site execution.
                </p>
            </div>
        </div>
    </PageFrame>
);

const Overline = ({ children, className = "" }) => (
    <p className={`text-[11px] tracking-[0.32em] uppercase text-[#B08056] font-sans ${className}`}>
        {children}
    </p>
);

const FlipbookNavContext = createContext({ jumpTo: () => {}, current: 0, total: 1 });

const BackToIndexFooterLink = () => {
    const { jumpTo, current, total } = useContext(FlipbookNavContext);
    if (current < 2 || current >= total - 1) return null;
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                jumpTo(1);
            }}
            data-testid="back-to-index-btn"
            aria-label="Back to Index"
            className="group relative z-30 pointer-events-auto flex items-center gap-2 px-2.5 py-1 -my-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#1F1A14]"
        >
            <span
                aria-hidden
                className="inline-block text-[#B08056] text-[12px] leading-none transition-transform duration-300 group-hover:-translate-x-1"
            >
                ↩
            </span>
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#7A6F5F] group-hover:text-[#1F1A14] font-sans transition-colors duration-300">
                Back to Index
            </span>
            <span className="font-serif italic text-[10.5px] text-[#B08056]/70">
                p. 02
            </span>
        </button>
    );
};

const PageFrame = ({ children, section, pageNumber, total }) => (
    <div
        className="relative w-full h-full bg-[#F1EBDF] overflow-hidden"
        style={{
            backgroundImage:
                "radial-gradient(1200px 800px at 20% 0%, rgba(255,255,255,0.55), transparent 60%), radial-gradient(900px 600px at 100% 100%, rgba(176,128,86,0.06), transparent 60%)",
        }}
    >
        <div
            className="absolute inset-0 pointer-events-none opacity-[0.06]"
            style={{
                backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.12 0 0 0 0 0.10 0 0 0 0 0.08 0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            }}
        />
        <div className="absolute top-8 md:top-10 left-8 md:left-14 right-8 md:right-14 flex items-center justify-between">
            <Overline>{section}</Overline>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#7A6F5F] font-sans">
                {STUDIO.name}
            </p>
        </div>
        <div className="absolute inset-0 pt-20 md:pt-24 pb-20 md:pb-24 px-8 md:px-14 overflow-hidden">
            {children}
        </div>
        <div className="absolute bottom-8 md:bottom-10 left-8 md:left-14 right-8 md:right-14 flex items-center justify-between border-t border-[#D9CFBE] pt-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#7A6F5F] font-sans">
                Portfolio · 2026
            </p>
            <BackToIndexFooterLink />
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#7A6F5F] font-sans">
                {String(pageNumber).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </p>
        </div>
    </div>
);

// ------- Static pages -------
const Cover = () => (
    <div className="relative w-full h-full overflow-hidden bg-[#F4F4F2] text-[#1E1E1E]">
        <div className="grid grid-cols-12 h-full">
            <div className="col-span-7 relative overflow-hidden">
                <img
                    src={NATURE_IMG}
                    alt="Calm interior with natural light and greenery"
                    className="w-full h-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/15" />
                <div className="absolute top-6 left-6 w-6 h-6 border-t border-l border-white/80" />
                <div className="absolute bottom-6 left-6 w-6 h-6 border-b border-l border-white/80" />
                <p className="absolute bottom-8 left-14 text-[10px] tracking-[0.32em] uppercase text-white/95 drop-shadow">
                    Interior · Design · Nature
                </p>

                {/* 3D preview stack */}
                <div className="absolute bottom-8 right-8 md:bottom-10 md:right-10 flex flex-col items-end gap-2 pointer-events-none">
                    <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-white/95 drop-shadow">
                        <span className="text-[#F0C99A]">◆</span>&nbsp;&nbsp;3D visualisation inside
                    </p>
                    <div className="relative h-[80px] w-[150px] md:h-[92px] md:w-[180px]">
                        <div
                            className="absolute top-1 left-0 w-[60%] h-[86%] bg-white/90 border border-white p-1 shadow-lg"
                            style={{ transform: "rotate(-6deg)" }}
                        >
                            <img
                                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/pya0ej0f_The%20Bricks%20Gallery%201.png"
                                alt="3D preview — Bricks Gallery"
                                className="w-full h-full object-cover"
                                loading="eager"
                            />
                        </div>
                        <div
                            className="absolute top-0 left-[22%] w-[60%] h-[86%] bg-white/90 border border-white p-1 shadow-lg"
                            style={{ transform: "rotate(2deg)" }}
                        >
                            <img
                                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/dh2g66xs_Kitchen%201.png"
                                alt="3D preview — Kitchen"
                                className="w-full h-full object-cover"
                                loading="eager"
                            />
                        </div>
                        <div
                            className="absolute top-2 right-0 w-[58%] h-[86%] bg-white/90 border border-white p-1 shadow-lg"
                            style={{ transform: "rotate(8deg)" }}
                        >
                            <img
                                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_minimal-design-62/artifacts/yoyin3a1_Kitchen%203.png"
                                alt="3D preview — Kitchen collage"
                                className="w-full h-full object-cover"
                                loading="eager"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-span-5 relative flex flex-col justify-between px-10 md:px-14 py-10 md:py-14 bg-gradient-to-b from-[#FAFAF8] via-[#F1F1EF] to-[#E7E7E4]">
                <div className="mt-6">
                    <p className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#6B6B68] leading-relaxed">
                        Interior Designer
                    </p>
                    <h1 className="mt-6 font-serif font-light text-[#1E1E1E] text-[38px] md:text-[54px] leading-[0.98] tracking-tight">
                        Maulik
                        <br />
                        <span className="italic text-[#4A4A48]">Prajapati.</span>
                    </h1>
                    <div className="mt-6 h-px w-14 bg-[#6B6B68]" />
                    <p className="mt-8 font-serif italic text-[#4A4A48] text-[18px] md:text-[22px] leading-[1.4] max-w-sm">
                        Professional journey started from 2013,
                        <br />
                        Creativity starts from 2026.
                    </p>
                </div>
                <div className="border-t border-[#D4D4D0] pt-6">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#6B6B68] mb-4">
                        Get in touch
                    </p>
                    <dl className="space-y-3">
                        <ContactRow label="Email" value={STUDIO.email} break_ />
                        <ContactRow label="Phone" value={STUDIO.phone} />
                        <ContactRow label="Address" value={STUDIO.location} />
                    </dl>
                </div>
            </div>
        </div>
    </div>
);

const ContactRow = ({ label, value, break_ = false }) => (
    <div className="flex items-baseline gap-3">
        <dt className="text-[10px] tracking-[0.28em] uppercase text-[#6B6B68] w-16 shrink-0">
            {label}
        </dt>
        <dd className={`font-sans text-[#1E1E1E] text-[13px] md:text-[14px] ${break_ ? "break-all" : ""}`}>
            {value}
        </dd>
    </div>
);

const BackCover = () => (
    <div
        className="relative w-full h-full flex flex-col items-center justify-center text-center px-12"
        style={{
            background:
                "linear-gradient(180deg, #241D15 0%, #1F1A14 55%, #14100C 100%)",
        }}
    >
        <div className="absolute inset-8 md:inset-12 border border-[#B08056]/40" />
        <p className="font-serif italic text-[#B08056] text-lg tracking-wide">
            End of portfolio
        </p>
        <div className="mt-8 mb-10 h-px w-24 bg-[#B08056]" />
        <h2 className="font-serif font-light text-[#F1EBDF] text-[42px] md:text-[56px] leading-tight">
            Thank you<br />for reading.
        </h2>
        <div className="mt-10 h-px w-24 bg-[#B08056]" />
        <p className="mt-10 font-serif italic text-[#D9CFBE]/80 text-base md:text-lg">
            — Maulik
        </p>
    </div>
);

const IntroPage = ({ pageNumber, total }) => (
    <PageFrame section="01 · Introduction" pageNumber={pageNumber} total={total}>
        <div className="grid grid-cols-12 gap-8 md:gap-12 h-full items-center">
            <div className="col-span-5 flex items-center h-full">
                <div className="w-full border border-[#B08056]/50 p-2 bg-[#EAE2D2]">
                    <div className="overflow-hidden bg-[#EAE2D2]">
                        <img
                            src={PROFILE_PHOTO}
                            alt="Maulik Prajapati — Interior Designer"
                            className="w-full aspect-[4/5] object-cover object-top"
                            loading="eager"
                        />
                    </div>
                    <p className="mt-3 text-[10px] tracking-[0.3em] uppercase text-[#7A6F5F] text-center">
                        Maulik Prajapati · Interior Designer
                    </p>
                </div>
            </div>
            <div className="col-span-7 flex flex-col justify-center">
                <h1 className="font-serif font-light text-[#1F1A14] text-[30px] md:text-[42px] leading-[1.05] tracking-tight">
                    <span className="uppercase">Make</span>{" "}
                    <em className="font-light italic text-[#7A6F5F] normal-case">creative</em>
                    <br />
                    <span className="uppercase">Useful</span>{" "}
                    <span className="normal-case">space.</span>
                </h1>
                <div className="mt-6 h-px w-12 bg-[#B08056]" />
                <div className="mt-6 space-y-4 max-w-xl">
                    <p className="font-sans text-[#4A4237] leading-[1.65] text-[13px] md:text-[14px] text-justify">
                        Business-minded Interior Designer with 12+ years of experience in
                        business operations, combined with a strong passion for interior design.
                    </p>
                    <p className="font-sans text-[#4A4237] leading-[1.65] text-[13px] md:text-[14px] text-justify">
                        Skilled in understanding client needs and delivering user-focused
                        spaces, with a strong inclination towards developing original,
                        concept-driven designs that reflect unique spatial solutions.
                    </p>
                </div>
            </div>
        </div>
    </PageFrame>
);

const ContactPage = ({ pageNumber, total }) => (
    <PageFrame section={`${String(pageNumber - 1).padStart(2, "0")} · Contact`} pageNumber={pageNumber} total={total}>
        <div className="grid grid-cols-12 gap-8 h-full">
            <div className="col-span-5 flex flex-col justify-center">
                <Overline>Get in touch</Overline>
                <h1 className="mt-4 font-serif font-light text-[#1F1A14] text-[32px] md:text-[46px] leading-[1.05] tracking-tight">
                    Let's begin<br />a conversation.
                </h1>
                <p className="mt-6 font-serif italic text-[#7A6F5F] max-w-sm">
                    Currently accepting a small number of residential and hospitality briefs.
                </p>
            </div>
            <div className="col-span-7 flex flex-col justify-center">
                <dl className="space-y-8 border-t border-[#D9CFBE] pt-10">
                    <div>
                        <dt className="text-[11px] tracking-[0.3em] uppercase text-[#B08056]">Email</dt>
                        <dd className="mt-2 font-serif text-[#1F1A14] text-[24px] md:text-[30px] break-all">
                            {STUDIO.email}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[11px] tracking-[0.3em] uppercase text-[#B08056]">Phone</dt>
                        <dd className="mt-2 font-serif text-[#1F1A14] text-[24px] md:text-[30px]">
                            {STUDIO.phone}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[11px] tracking-[0.3em] uppercase text-[#B08056]">Address</dt>
                        <dd className="mt-2 font-serif text-[#1F1A14] text-[24px] md:text-[30px]">
                            {STUDIO.location}
                        </dd>
                    </div>
                </dl>
            </div>
        </div>
    </PageFrame>
);

// ------- Project pages (generic) -------
const ProjectIntro = ({ project, projectNumber, pageNumber, total }) => (
    <PageFrame
        section={`${String(projectNumber + 1).padStart(2, "0")} · Featured project · ${project.category}`}
        pageNumber={pageNumber}
        total={total}
    >
        <div className="grid grid-cols-12 gap-8 md:gap-10 h-full items-stretch">
            {/* Text — left, narrow */}
            <div className="col-span-5 flex flex-col justify-center min-h-0 py-2">
                <Overline>{project.category.split(" · ")[0]} project · {project.year}</Overline>
                <h1 className="mt-3 font-serif font-light text-[#1F1A14] text-[22px] md:text-[30px] leading-[1.05] tracking-tight">
                    {project.titleLines.map((line, i) => (
                        <span key={i}>
                            {line}
                            {i < project.titleLines.length - 1 ? <br /> : null}
                        </span>
                    ))}
                </h1>
                <p className="mt-3 font-serif italic text-[#7A6F5F] text-[12px] md:text-[14px] leading-snug">
                    {project.subtitle}
                </p>
                <div className="mt-3 h-px w-12 bg-[#B08056]" />
                <div className="mt-3 space-y-2">
                    {project.description.map((p, i) => (
                        <p
                            key={i}
                            className="font-sans text-[#4A4237] leading-[1.5] text-[11px] md:text-[12.5px] text-justify"
                        >
                            {p}
                        </p>
                    ))}
                </div>
                {project.brief ? (
                    <dl className="mt-3 divide-y divide-[#D9CFBE] border-y border-[#D9CFBE]" data-testid={`project-brief-${project.id}`}>
                        {project.brief.map((row, i) => (
                            <div key={i} className="py-1 flex items-baseline gap-3">
                                <dt className="text-[8.5px] tracking-[0.26em] uppercase text-[#B08056] w-24 shrink-0">
                                    {row.k}
                                </dt>
                                <dd className="font-serif text-[#1F1A14] text-[11.5px] md:text-[12.5px] flex-1 leading-snug">
                                    {row.v}
                                </dd>
                            </div>
                        ))}
                    </dl>
                ) : null}
            </div>

            {/* Material board — right, large */}
            <div className="col-span-7 h-full flex flex-col justify-center min-h-0">
                <div
                    className="border border-[#D9CFBE] bg-[#EAE2D2] p-2 flex items-center justify-center flex-1 min-h-0"
                >
                    <img
                        src={project.materialBoard}
                        alt={`Material board — ${project.titleLines.join(" ")}`}
                        className="object-contain block"
                        style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto" }}
                        loading="lazy"
                    />
                </div>
                <p className="mt-3 text-[10px] tracking-[0.3em] uppercase text-[#7A6F5F] text-center shrink-0">
                    Material board · Palette &amp; finishes
                </p>
            </div>
        </div>
    </PageFrame>
);

const ProjectGallery = ({
    project,
    projectNumber,
    startIndex,
    items,
    galleryTotal,
    pageNumber,
    total,
}) => (
    <PageFrame
        section={`${String(projectNumber + 1).padStart(2, "0")} · ${project.category} · Drawings`}
        pageNumber={pageNumber}
        total={total}
    >
        <div className="h-full flex flex-col justify-center min-h-0">
            <div className={`grid gap-6 md:gap-10 flex-1 min-h-0 ${items.length === 1 ? "grid-cols-2" : "grid-cols-2"}`}>
                {items.map((g, i) => {
                    const n = startIndex + i + 1;
                    return (
                        <figure key={n} className="flex flex-col min-h-0 h-full">
                            <div className="border border-[#D9CFBE] bg-[#EAE2D2] p-2 flex items-center justify-center flex-1 min-h-0">
                                <img
                                    src={g.src}
                                    alt={g.title}
                                    className="object-contain block"
                                    style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto" }}
                                    loading="lazy"
                                />
                            </div>
                            <figcaption className="mt-3 flex items-baseline justify-between border-t border-[#D9CFBE] pt-3 shrink-0">
                                <span className="text-[10px] tracking-[0.3em] uppercase text-[#B08056]">
                                    {String(n).padStart(2, "0")} /{" "}
                                    {String(galleryTotal).padStart(2, "0")}
                                </span>
                                <span className="font-serif text-[#1F1A14] text-[14px] md:text-[16px] text-right">
                                    {g.title}
                                </span>
                            </figcaption>
                        </figure>
                    );
                })}
                {items.length === 1 ? <div /> : null}
            </div>
        </div>
    </PageFrame>
);

const Full3DPage = ({ sectionLabel, image, index, threeDTotal, pageNumber, total }) => (
    <PageFrame
        section={`${sectionLabel} · ${String(index).padStart(2, "0")} / ${String(threeDTotal).padStart(2, "0")}`}
        pageNumber={pageNumber}
        total={total}
    >
        <div className="h-full flex flex-col pb-4 min-h-0">
            <div className="flex-1 min-h-0 flex items-center justify-center">
                <div
                    className="border border-[#D9CFBE] bg-[#EAE2D2] p-2 flex items-center justify-center"
                    style={{ height: "100%", maxWidth: "100%" }}
                >
                    <img
                        src={image.src}
                        alt={image.title}
                        className="object-contain block"
                        style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto" }}
                        loading="lazy"
                    />
                </div>
            </div>
            <p className="mt-2 text-center text-[10px] tracking-[0.3em] uppercase text-[#7A6F5F] shrink-0">
                {image.title}
            </p>
        </div>
    </PageFrame>
);

const ThreeDIntroPage = ({ groups, pageNumber, total }) => (
    <PageFrame section="10 · 3D Visualisation" pageNumber={pageNumber} total={total}>
        <div className="grid grid-cols-12 gap-8 md:gap-12 h-full items-center">
            <div className="col-span-5 flex flex-col justify-center">
                <Overline>Chapter · Ten</Overline>
                <h1 className="mt-5 font-serif font-light text-[#1F1A14] text-[36px] md:text-[56px] leading-[1.02] tracking-tight">
                    3D
                    <br />
                    <span className="italic text-[#7A6F5F]">Visualisation.</span>
                </h1>
                <div className="mt-6 h-px w-14 bg-[#B08056]" />
                <p className="mt-6 font-serif italic text-[#7A6F5F] text-[14px] md:text-[16px] leading-relaxed max-w-sm">
                    Photoreal renders — a visual continuation of the studio's
                    drawings into space, light and mood.
                </p>
                <p className="mt-4 font-sans text-[#4A4237] text-[12.5px] md:text-[13.5px] leading-[1.65] max-w-sm text-justify">
                    Two projects are showcased in this chapter, translating
                    plans and elevations into inhabited, textured scenes.
                </p>
            </div>

            <div className="col-span-7 flex flex-col justify-center">
                <div className="border-y border-[#D9CFBE]">
                    {groups.map((g, i) => (
                        <div
                            key={g.group}
                            className={`py-6 md:py-8 flex items-baseline gap-6 ${i > 0 ? "border-t border-[#D9CFBE]" : ""}`}
                            data-testid={`3d-intro-item-${i}`}
                        >
                            <span className="font-serif text-[#B08056] text-[22px] md:text-[28px] leading-none w-14 shrink-0">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <div className="flex-1">
                                <p className="text-[10px] tracking-[0.32em] uppercase text-[#7A6F5F] mb-1.5">
                                    Project · {String(g.images.length).padStart(2, "0")} render{g.images.length > 1 ? "s" : ""}
                                </p>
                                <h3 className="font-serif font-light text-[#1F1A14] text-[22px] md:text-[28px] leading-[1.1]">
                                    {g.group}
                                </h3>
                                <p className="mt-2 font-serif italic text-[#7A6F5F] text-[12px] md:text-[13px]">
                                    {g.images.map((im) => im.title).join(" · ")}
                                </p>
                            </div>
                            <div className="hidden md:flex gap-1 shrink-0">
                                {g.images.slice(0, 3).map((im, k) => (
                                    <div
                                        key={k}
                                        className="w-11 h-11 border border-[#D9CFBE] bg-[#EAE2D2] p-0.5 overflow-hidden"
                                    >
                                        <img
                                            src={im.src}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <p className="mt-6 text-right font-serif italic text-[#B08056] text-[12px]">
                    Turn the page →
                </p>
            </div>
        </div>
    </PageFrame>
);

// ------- Build the pages list -------
const buildPages = () => {
    const pages = [];
    // We build first, then inject the index page after cover with computed page numbers.
    const push = (key, render) => pages.push({ key, render });

    push("cover", () => <Cover />);
    // Placeholder for index — we'll insert it after building others so we know page numbers.
    push("__index__", null);
    push("intro", (n, t) => <IntroPage pageNumber={n} total={t} />);

    const marks = []; // { label, page } for the index

    // Academic projects
    PROJECTS.forEach((project, pIdx) => {
        marks.push({ label: `${project.category} — ${project.titleLines.join(" ")}`.replace(/\.$/, ""), page: pages.length + 1, pageIndex: pages.length });
        push(`${project.id}-intro`, (n, t) => (
            <ProjectIntro project={project} projectNumber={pIdx + 1} pageNumber={n} total={t} />
        ));
        for (let i = 0; i < project.gallery.length; i += 2) {
            const items = project.gallery.slice(i, i + 2);
            const startIndex = i;
            push(`${project.id}-gallery-${i / 2 + 1}`, (n, t) => (
                <ProjectGallery
                    project={project}
                    projectNumber={pIdx + 1}
                    startIndex={startIndex}
                    items={items}
                    galleryTotal={project.gallery.length}
                    pageNumber={n}
                    total={t}
                />
            ));
        }
    });

    // Ongoing — Residential
    marks.push({ label: "Ongoing : Residential Project", page: pages.length + 1, pageIndex: pages.length });
    RESIDENTIAL_PROJECTS.forEach((project) => {
        push(`${project.id}-intro`, (n, t) => (
            <ProjectIntro project={project} projectNumber={0} pageNumber={n} total={t} />
        ));
        for (let i = 0; i < project.gallery.length; i += 2) {
            const items = project.gallery.slice(i, i + 2);
            const startIndex = i;
            push(`${project.id}-gallery-${i / 2 + 1}`, (n, t) => (
                <ProjectGallery
                    project={project}
                    projectNumber={0}
                    startIndex={startIndex}
                    items={items}
                    galleryTotal={project.gallery.length}
                    pageNumber={n}
                    total={t}
                />
            ));
        }
    });

    // Commercial
    marks.push({ label: "Ongoing commercial project", page: pages.length + 1, pageIndex: pages.length });
    COMMERCIAL_PROJECTS.forEach((project) => {
        push(`${project.id}-intro`, (n, t) => (
            <ProjectIntro project={project} projectNumber={0} pageNumber={n} total={t} />
        ));
        for (let i = 0; i < (project.gallery?.length ?? 0); i += 2) {
            const items = project.gallery.slice(i, i + 2);
            const startIndex = i;
            push(`${project.id}-gallery-${i / 2 + 1}`, (n, t) => (
                <ProjectGallery
                    project={project}
                    projectNumber={0}
                    startIndex={startIndex}
                    items={items}
                    galleryTotal={project.gallery.length}
                    pageNumber={n}
                    total={t}
                />
            ));
        }
    });

    // 3D Visualisation — grouped chapter (Bricks Gallery + Kitchen Project)
    marks.push({ label: "3D Visualisation", page: pages.length + 1, pageIndex: pages.length });
    push("3d-intro", (n, t) => (
        <ThreeDIntroPage groups={THREE_D_VIS} pageNumber={n} total={t} />
    ));
    THREE_D_VIS.forEach((grp) => {
        const groupSlug = grp.group.toLowerCase().replace(/\s+/g, "-");
        grp.images.forEach((img, i) => {
            push(`3d-${groupSlug}-${i + 1}`, (n, t) => (
                <Full3DPage
                    sectionLabel={`3D Visualisation · ${grp.group}`}
                    image={img}
                    index={i + 1}
                    threeDTotal={grp.images.length}
                    pageNumber={n}
                    total={t}
                />
            ));
        });
    });

    // Contact
    marks.push({ label: "Contact", page: pages.length + 1, pageIndex: pages.length });
    push("contact", (n, t) => <ContactPage pageNumber={n} total={t} />);

    push("back", () => <BackCover />);

    // Prepend intro & academic works section markers
    const introMark = { label: "Introduction", page: 3, pageIndex: 2 };
    const academicMark = { label: "Academic projects", page: 4, pageIndex: 3 };
    const allMarks = [introMark, academicMark, ...marks];

    // Inject IndexPage at position 1 (page 2)
    const indexIdx = pages.findIndex((p) => p.key === "__index__");
    pages[indexIdx] = {
        key: "index",
        render: (n, t, jumpTo) => <IndexPage pageNumber={n} total={t} sections={allMarks} onJump={jumpTo} />,
    };
    return pages;
};

const PAGES = buildPages();

// ------- Flipbook shell -------
export const Flipbook = () => {
    const total = PAGES.length;
    const [current, setCurrent] = useState(0);
    const [dir, setDir] = useState(1);

    const next = useCallback(() => {
        setCurrent((i) => {
            if (i >= total - 1) return i;
            setDir(1);
            return i + 1;
        });
    }, [total]);

    const prev = useCallback(() => {
        setCurrent((i) => {
            if (i <= 0) return i;
            setDir(-1);
            return i - 1;
        });
    }, []);

    const jumpTo = useCallback(
        (targetIdx) => {
            if (typeof targetIdx !== "number") return;
            const clamped = Math.max(0, Math.min(total - 1, targetIdx));
            setCurrent((cur) => {
                setDir(clamped >= cur ? 1 : -1);
                return clamped;
            });
        },
        [total]
    );

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev]);

    const [rotateForMobile, setRotateForMobile] = useState(false);
    useEffect(() => {
        const check = () => {
            const isPortrait = window.innerHeight > window.innerWidth;
            const isMobile = window.innerWidth < 900;
            setRotateForMobile(isPortrait && isMobile);
        };
        check();
        window.addEventListener("resize", check);
        window.addEventListener("orientationchange", check);
        return () => {
            window.removeEventListener("resize", check);
            window.removeEventListener("orientationchange", check);
        };
    }, []);

    // ------- PDF export -------
    const [captureIdx, setCaptureIdx] = useState(null);
    const [pdfProgress, setPdfProgress] = useState({ generating: false, current: 0, total: 0 });
    const captureSlotRef = useRef(null);

    const downloadPdf = useCallback(async () => {
        if (pdfProgress.generating) return;
        setPdfProgress({ generating: true, current: 0, total });
        try {
            const [{ jsPDF }, html2canvasMod] = await Promise.all([
                import("jspdf"),
                import("html2canvas"),
            ]);
            const html2canvas = html2canvasMod.default;
            const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
            const pdfW = pdf.internal.pageSize.getWidth();
            const pdfH = pdf.internal.pageSize.getHeight();

            for (let i = 0; i < total; i++) {
                setCaptureIdx(i);
                // wait for DOM & images to settle
                await new Promise((r) => setTimeout(r, 350));
                // wait for all images in the slot to be loaded
                if (captureSlotRef.current) {
                    const imgs = captureSlotRef.current.querySelectorAll("img");
                    await Promise.all(
                        Array.from(imgs).map(
                            (img) =>
                                img.complete && img.naturalWidth > 0
                                    ? Promise.resolve()
                                    : new Promise((res) => {
                                          img.onload = res;
                                          img.onerror = res;
                                          setTimeout(res, 4000);
                                      })
                        )
                    );
                }
                const canvas = await html2canvas(captureSlotRef.current, {
                    scale: 1.6,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: "#F1EBDF",
                    logging: false,
                });
                const imgData = canvas.toDataURL("image/jpeg", 0.85);
                if (i > 0) pdf.addPage("a3", "landscape");
                pdf.addImage(imgData, "JPEG", 0, 0, pdfW, pdfH, undefined, "FAST");
                setPdfProgress({ generating: true, current: i + 1, total });
            }

            pdf.save("Maulik-Prajapati-Portfolio-2026.pdf");
        } catch (err) {
            console.error("PDF generation failed", err);
        } finally {
            setCaptureIdx(null);
            setPdfProgress({ generating: false, current: 0, total: 0 });
        }
    }, [pdfProgress.generating, total]);

    const variants = {
        enter: (d) => ({ rotateY: d > 0 ? -90 : 90, opacity: 0, x: d > 0 ? 60 : -60 }),
        center: { rotateY: 0, opacity: 1, x: 0 },
        exit: (d) => ({ rotateY: d > 0 ? 90 : -90, opacity: 0, x: d > 0 ? -60 : 60 }),
    };

    return (
        <FlipbookNavContext.Provider value={{ jumpTo, current, total }}>
        <div
            className={
                rotateForMobile
                    ? "fixed top-1/2 left-1/2 bg-[#EAE2D2] text-[#1F1A14] flex items-center justify-center overflow-hidden"
                    : "w-full min-h-screen bg-[#EAE2D2] text-[#1F1A14] flex items-center justify-center px-4 py-8"
            }
            data-testid="flipbook-root"
            style={
                rotateForMobile
                    ? {
                          width: "100vh",
                          height: "100vw",
                          transform: "translate(-50%, -50%) rotate(90deg)",
                          backgroundImage:
                              "radial-gradient(1200px 800px at 10% 0%, rgba(255,255,255,0.35), transparent 60%), radial-gradient(900px 700px at 100% 100%, rgba(31,26,20,0.08), transparent 60%)",
                      }
                    : {
                          backgroundImage:
                              "radial-gradient(1200px 800px at 10% 0%, rgba(255,255,255,0.35), transparent 60%), radial-gradient(900px 700px at 100% 100%, rgba(31,26,20,0.08), transparent 60%)",
                      }
            }
        >
            <div className="fixed top-6 md:top-8 left-6 md:left-10 z-30 flex items-center gap-3 pointer-events-none">
                <div className="w-9 h-9 border border-[#1F1A14] flex items-center justify-center">
                    <span className="font-serif text-[#1F1A14] text-lg leading-none">MP</span>
                </div>
                <div>
                    <p className="font-serif text-[#1F1A14] text-[15px] leading-tight">
                        Maulik Prajapati
                    </p>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#7A6F5F]">
                        Interior Designer
                    </p>
                </div>
            </div>

            <div className="fixed top-6 md:top-8 right-6 md:right-10 z-30 text-right pointer-events-none">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#7A6F5F]">
                    Portfolio · 2026
                </p>
                <p className="font-serif text-[#1F1A14] text-lg mt-1">
                    {String(current + 1).padStart(2, "0")}
                    <span className="text-[#B08056] mx-1">/</span>
                    {String(total).padStart(2, "0")}
                </p>
            </div>

            <button
                type="button"
                onClick={downloadPdf}
                disabled={pdfProgress.generating}
                data-testid="download-pdf-btn"
                className="group fixed bottom-6 md:bottom-8 right-6 md:right-10 z-40 flex items-center gap-2 pl-3 pr-3.5 py-2 bg-[#1F1A14] hover:bg-[#2A231A] border border-[#1F1A14] transition-all duration-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#B08056] shadow-[0_8px_22px_-10px_rgba(31,26,20,0.55)] disabled:cursor-not-allowed disabled:opacity-70"
            >
                <Download size={13} className="text-[#B08056] transition-transform duration-300 group-hover:translate-y-0.5" />
                <span className="font-sans text-[9.5px] md:text-[10px] tracking-[0.34em] uppercase text-[#F1EBDF]">
                    {pdfProgress.generating ? "Preparing…" : "Download PDF"}
                </span>
            </button>

            <div className="relative" style={{ perspective: "1800px" }}>
                <div
                    className="relative shadow-[0_40px_80px_-30px_rgba(31,26,20,0.45),0_10px_30px_-10px_rgba(31,26,20,0.35)]"
                    style={{
                        aspectRatio: "1.4142 / 1",
                        width: rotateForMobile
                            ? "min(94vh, calc(84vw * 1.4142), 1300px)"
                            : "min(94vw, calc(84vh * 1.4142), 1300px)",
                        transformStyle: "preserve-3d",
                    }}
                >
                    <div className="absolute inset-0 bg-[#E4DAC5]" />
                    <AnimatePresence initial={false} custom={dir} mode="wait">
                        <motion.div
                            key={PAGES[current].key}
                            custom={dir}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                rotateY: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                                opacity: { duration: 0.4 },
                                x: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                            }}
                            className="absolute inset-0 z-20 pointer-events-none"
                            style={{
                                transformOrigin: dir > 0 ? "left center" : "right center",
                                transformStyle: "preserve-3d",
                                backfaceVisibility: "hidden",
                            }}
                            data-testid={`page-${PAGES[current].key}`}
                        >
                            {PAGES[current].render(current + 1, total, jumpTo)}
                        </motion.div>
                    </AnimatePresence>

                    <button
                        type="button"
                        onClick={prev}
                        aria-label="Previous page"
                        data-testid="prev-zone"
                        disabled={current === 0}
                        className="group absolute left-0 top-0 h-full w-1/2 z-10 focus:outline-none disabled:cursor-not-allowed"
                    >
                        <div className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <ChevronLeft size={16} className="text-[#1F1A14]" />
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[#1F1A14]">
                                Prev
                            </span>
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={next}
                        aria-label="Next page"
                        data-testid="next-zone"
                        disabled={current === total - 1}
                        className="group absolute right-0 top-0 h-full w-1/2 z-10 focus:outline-none disabled:cursor-not-allowed"
                    >
                        <div className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[#1F1A14]">
                                Next
                            </span>
                            <ChevronRight size={16} className="text-[#1F1A14]" />
                        </div>
                    </button>
                </div>
            </div>

            <p className="fixed bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase text-[#7A6F5F] pointer-events-none">
                Click left / right of the book · or use ← →
            </p>

            {/* Hidden PDF capture slot — one page at a time */}
            <div
                aria-hidden
                style={{
                    position: "fixed",
                    left: "-100000px",
                    top: 0,
                    width: "1400px",
                    height: `${1400 / 1.4142}px`,
                    pointerEvents: "none",
                    background: "#F1EBDF",
                    overflow: "hidden",
                }}
            >
                <FlipbookNavContext.Provider value={{ jumpTo: () => {}, current: 0, total }}>
                    <div
                        ref={captureSlotRef}
                        style={{
                            width: "1400px",
                            height: `${1400 / 1.4142}px`,
                            background: "#F1EBDF",
                        }}
                    >
                        {captureIdx !== null
                            ? PAGES[captureIdx].render(captureIdx + 1, total, () => {})
                            : null}
                    </div>
                </FlipbookNavContext.Provider>
            </div>

            {/* PDF generation progress overlay */}
            {pdfProgress.generating && (
                <div
                    className="fixed inset-0 z-[60] bg-[#1F1A14]/85 backdrop-blur-sm flex items-center justify-center"
                    data-testid="pdf-progress-overlay"
                >
                    <div className="bg-[#F1EBDF] border border-[#B08056]/40 px-8 py-7 max-w-sm w-full mx-6 shadow-2xl">
                        <p className="text-[10px] tracking-[0.34em] uppercase text-[#B08056] mb-3">
                            Preparing your PDF
                        </p>
                        <h3 className="font-serif font-light text-[#1F1A14] text-[24px] leading-tight">
                            Rendering page{" "}
                            <span className="italic text-[#B08056]">
                                {String(pdfProgress.current).padStart(2, "0")}
                            </span>{" "}
                            of {String(pdfProgress.total).padStart(2, "0")}
                        </h3>
                        <div className="mt-5 h-[3px] w-full bg-[#EAE2D2]">
                            <div
                                className="h-full bg-[#1F1A14] transition-all duration-300"
                                style={{
                                    width: `${pdfProgress.total ? (pdfProgress.current / pdfProgress.total) * 100 : 0}%`,
                                }}
                            />
                        </div>
                        <p className="mt-4 font-serif italic text-[#7A6F5F] text-[13px]">
                            Please keep this tab open — the file will download automatically.
                        </p>
                    </div>
                </div>
            )}
        </div>
        </FlipbookNavContext.Provider>
    );
};
